import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../../services/api';

interface AddressAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (address: string, placeId: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

export default function AddressAutocompleteInput({
  value,
  onChange,
  onSelect,
  placeholder = 'Search address or location...',
  className = '',
  inputClassName = '',
  disabled = false,
  autoFocus = false,
}: AddressAutocompleteInputProps) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState<Array<{ description: string; place_id: string }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sync internal state when external value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const fetchSuggestions = (query: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const res: any = await apiService.getAddressAutocomplete(query.trim());
        const preds = res?.predictions || res?.data?.predictions || [];
        setSuggestions(preds);
        setShowSuggestions(preds.length > 0);
      } catch (err) {
        console.error('Google Maps address autocomplete error:', err);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
    fetchSuggestions(val);
  };

  const handleSelect = (suggestion: { description: string; place_id: string }) => {
    setInputValue(suggestion.description);
    onChange(suggestion.description);
    if (onSelect) {
      onSelect(suggestion.description, suggestion.place_id);
    }
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative flex items-center">
        <i className="ri-map-pin-line absolute left-3 text-gray-400 text-sm pointer-events-none"></i>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true);
            else if (inputValue.trim().length >= 2) fetchSuggestions(inputValue);
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
          className={`w-full text-sm text-gray-800 bg-white border border-gray-300 rounded-lg pl-9 pr-9 py-2 focus:outline-none focus:border-[#0b7f8f] focus:ring-1 focus:ring-[#0b7f8f] transition-colors ${inputClassName}`}
        />
        {isLoading ? (
          <i className="ri-loader-4-line animate-spin absolute right-3 text-[#0b7f8f] text-sm pointer-events-none"></i>
        ) : inputValue ? (
          <button
            type="button"
            onClick={() => {
              setInputValue('');
              onChange('');
              setSuggestions([]);
              setShowSuggestions(false);
            }}
            className="absolute right-3 text-gray-400 hover:text-gray-600 cursor-pointer p-0.5"
            title="Clear"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        ) : null}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl py-1 focus:outline-none divide-y divide-gray-100 text-left">
          {suggestions.map((suggestion) => (
            <li
              key={suggestion.place_id}
              onClick={() => handleSelect(suggestion)}
              className="px-3.5 py-2.5 text-xs text-gray-700 hover:bg-[#0b7f8f]/5 hover:text-[#0b7f8f] cursor-pointer flex items-start gap-2.5 transition-colors"
            >
              <i className="ri-map-pin-2-fill text-[#0b7f8f] text-sm mt-0.5 flex-shrink-0"></i>
              <span className="font-medium text-left leading-relaxed">{suggestion.description}</span>
            </li>
          ))}
          <li className="px-3 py-1.5 bg-gray-50 text-[10px] text-gray-400 flex items-center justify-between">
            <span>Powered by Google Maps</span>
            <i className="ri-google-fill text-xs"></i>
          </li>
        </ul>
      )}
    </div>
  );
}
